import { useCallback, useLayoutEffect, useState } from "react";

/**
 * Max height that lets a scroll container run from where it sits down to the
 * bottom of the area it scrolls in — replacing hand-tuned `calc(100vh - Npx)`
 * caps.
 *
 * A constant has to encode the height of everything above the element (context
 * line, page header, tab strip, toolbar) *and* everything the page puts below
 * it (a pagination bar). Every one of those moves independently — a filter row
 * wraps, a banner appears, pagination comes and goes — and the constant is then
 * wrong in the one direction that shows: a band at the bottom the list can
 * never fill. Both sides are measured here instead, the trailing one by adding
 * up the element's own following siblings.
 *
 * Nothing is reserved for the floating chat launcher. That belongs INSIDE the
 * scroller, as a `ChatLauncherSpacer` after the content: reserving it here
 * would stop the list short of the window and recreate the blank band in
 * miniature, where a trailing spacer lets the list reach the edge and still
 * scroll its last row clear of the pill.
 *
 * The measurement is scroll-invariant on purpose. Taking `top` straight off
 * `getBoundingClientRect()` would grow the cap every time the page is scrolled
 * down, and a taller element makes a longer page, so the two feed each other.
 * Offsets are therefore taken relative to the scroll host and corrected by its
 * `scrollTop`, which does not move when the host scrolls.
 *
 * Attach with the returned `measureRef` rather than a `useRef` object: the
 * element is usually rendered conditionally (an empty list renders a
 * placeholder instead), and a ref object gives the hook no signal when the real
 * node finally arrives. A callback ref does, and it composes — a caller that
 * also needs an imperative handle (the virtualizer wants one) can assign its
 * own ref inside its own callback and forward the node here.
 *
 * `maxHeight` is `undefined` until the first measurement. That happens in a
 * layout effect, so it is applied before the browser paints and there is no
 * visible jump from the uncapped first render.
 */
export function useFillToBottomHeight(): {
  measureRef: (element: HTMLElement | null) => void;
  maxHeight: number | undefined;
} {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useLayoutEffect(() => {
    if (!element) return;

    const measure = () => {
      let below = 0;
      for (let node = element.nextElementSibling; node; node = node.nextElementSibling) {
        below += node.getBoundingClientRect().height;
      }
      // `[data-sidebar-content]` is the building shell's scrolling column. Off
      // that shell (the child-documents dialog reuses this table), fall back to
      // the viewport — inside a dialog that is a ceiling the dialog's own
      // max-height already sits under, so the cap is inert rather than wrong.
      const host = element.closest<HTMLElement>("[data-sidebar-content]");
      if (!host) {
        setMaxHeight(Math.max(0, window.innerHeight - element.getBoundingClientRect().top - below));
        return;
      }
      const top =
        element.getBoundingClientRect().top - host.getBoundingClientRect().top + host.scrollTop;
      setMaxHeight(Math.max(0, host.clientHeight - top - below));
    };

    measure();
    window.addEventListener("resize", measure);
    // Anything above or below the element changing height moves its bounds.
    // Watching the document catches that without naming which ancestor grew.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(document.documentElement);

    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [element]);

  const measureRef = useCallback((node: HTMLElement | null) => setElement(node), []);

  // With no element there is nothing measured, so the cap is dropped during
  // render rather than written back from the effect: the last measurement
  // belongs to an element that is gone, and applying it to whatever mounts
  // next would cap it at a height nobody measured.
  return { measureRef, maxHeight: element ? maxHeight : undefined };
}
